<template>
  <div class="infocard-wrapper">
    <div v-show="isHidden" class="restore-infocard">
      <a
        href="#!"
        class="pointer"
        title="Restaurar card de informação"
        v-on:click.prevent="show"
      >
        <i class="material-icons left">help_outline</i>
        <!-- <span class="title">{{ title }}</span> -->
      </a>
    </div>
    <BaseCard
      v-show="!isHidden"
      v-bind:id="id"
      v-bind="infoCard"
      class="infocard"
    >
      <template v-slot:card-title>
        <span class="card-title">
          <i class="material-icons left">help_outline</i> {{ title }}
          <a
            href="#!"
            class="pointer right"
            title="Esconder card de informação"
            v-on:click.prevent="hide"
            ><i class="material-icons right">cancel</i></a
          >
        </span>
      </template>
      <template v-slot:card-content>
        <slot>{{ content }}</slot>
      </template>
    </BaseCard>
  </div>
</template>

<script>
/**
 * @module components/common/InfoCard/InfoCard
 * @category Componentes comuns
 * @summary _Single File Component_ (SFC) de _card_ de informações para o sistema.
 *
 * @description
 * Pequeno componente utilitário para se criar _cards_ com informações
 * explicativas pelo sistema.
 *
 * O _slot_ padrão recebe o conteúdo do _card_. A _prop_ `title` recebe o
 * título que aparecerá no _card_.
 *
 * Utiliza o estado global do sistema para armazenar o estado do componente,
 * i.e., se ele encontra-se oculto ou não. Para correto funcionamento, deve-se
 * preencher a _prop_ `id` devidamente.
 *
 * @requires module:components/base/BaseCard/BaseCard
 * @requires module:constants.INFO_CARD_BG_COLOR
 * @requires module:constants.INFO_CARD_BG_LUMINOSITY
 * @requires module:constants.INFO_CARD_TEXT_COLOR
 * @requires module:utils/lang.isEmpty
 *
 * @vue-prop {string} id - Vide {@link module:components/base/BaseCard/BaseCard BaseCard}.
 * @vue-prop {string} [title] - Título do _card_. Como alternativa, pode-se
 * utilizar o _slot_ de nome "card-title".
 * @vue-prop {string} [content] - Conteúdo do _card_. Como alternativa, pode-se
 * utilizar o _slot_ de nome "card-content".
 *
 * @example
 * <InfoCard id="infocard-cuidado" title="CUIDADO!">
 *   Esta ação é irreversível. Contate nosso suporte.
 * </InfoCard>
 */
import {
  INFO_CARD_BG_COLOR,
  INFO_CARD_BG_LUMINOSITY,
  INFO_CARD_TEXT_COLOR
} from "../../../constants";
import { isEmpty } from "../../../utils/lang";
import BaseCard from "../../base/BaseCard/BaseCard.vue";

const STORAGE_DEFAULT_OBJ = { isHidden: false };

const storeInfoCard = function (store, id, value) {
  store.commit("setInfoCard", { id, value });
};

const isHidden = function (store, id) {
  if (!store || isEmpty(id)) {
    return false;
  }

  let value = store.state.infoCards[id];

  if (!value) {
    value = STORAGE_DEFAULT_OBJ;

    storeInfoCard(store, id, value);
  }

  return value.isHidden;
};

const toggle = function (store, id, isHidden) {
  if (!store || isEmpty(id)) {
    return;
  }

  const value = store.state.infoCards[id];

  if (value && value.isHidden !== isHidden) {
    value.isHidden = isHidden;

    storeInfoCard(store, id, value);
  }
};

export default {
  name: "InfoCard",
  components: {
    BaseCard
  },
  inheritAttrs: false,
  props: {
    id: {
      type: String,
      require: true
    },

    title: String,
    content: String
  },
  data() {
    return {
      isHidden: false,
      infoCard: {
        color: INFO_CARD_BG_COLOR,
        luminosity: INFO_CARD_BG_LUMINOSITY,
        textColor: INFO_CARD_TEXT_COLOR
      }
    };
  },
  watch: {
    "$store.state.infoCards"(newValue) {
      if (newValue) {
        const value = newValue[this.id];

        if (value && value.isHidden !== this.isHidden) {
          this.isHidden = value.isHidden;
        }
      }
    }
  },
  beforeCreate() {
    if (!this.$store) {
      window.console.warn(
        "Um gerenciador de estados não foi definido. Não será possível restaurar a exibição do infocard via reatividade do Vue!"
      );
      return;
    }
  },
  created() {
    this.isHidden = isHidden(this.$store, this.id);
  },
  methods: {
    hide() {
      this.isHidden = true;
      toggle(this.$store, this.id, true);
    },
    show() {
      this.isHidden = false;
      toggle(this.$store, this.id, false);
    }
  }
};
</script>

<style scoped>
.pointer {
  cursor: pointer;
}

.restore-infocard {
  display: flex;
  margin-bottom: 8px;
  line-height: 32px;
  font-size: 24px;
  font-weight: 300;
}
.restore-infocard a {
  color: inherit;
}
.restore-infocard i {
  line-height: inherit;
}

.infocard /deep/ a {
  color: inherit;
  font-weight: 700;
}
</style>
